<template>
	<v-container class="evaluation">
		<v-layout>
			<v-btn @click="openProjectView" class="no-print">
				Podgląd wniosku
			</v-btn>
			<v-spacer></v-spacer>
			<v-btn class="mr-3 no-print" @click="print">Wydrukuj</v-btn>
		</v-layout>

		<v-card-title class="mt-3 mb-3">
			<div>
				<h2 class="mb-3">Opinia ekspercka</h2>
				<div class="evaluation-card evaluation-font-size">
					<div>
						<strong class="mr-3">Nazwa placówki:</strong> {{viewData.agencyName}}
					</div>
					<div>
						<strong class="mr-3">Nazwa projektu:</strong> {{viewData.agencyProjectTitle}}
					</div>
					<div>
						<strong class="mr-3">Numer wniosku:</strong> {{viewData.agencyProjectCode}}
					</div>
					<div>
						<strong class="mr-3">Suma kontrolna:</strong> {{viewData.controlSum}}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Wnioskowana kwota:</strong> {{formatMoney(viewData.mszFundings)}} {{viewData.currencyCode}}
					</div>
					<div class="mt-3">
						<strong class="mr-3">Kraj:</strong> {{viewData.country}}
					</div>
					<div v-if="viewData.extraCountries.length > 0">
						<strong class="mr-3">Kraje dodatkowe:</strong> {{viewData.extraCountries.join(', ')}}
					</div>
					<div v-if="viewData.author">
						<strong class="mr-3">Autor:</strong> {{viewData.author}}
					</div>
					<div v-if="viewData.firstSendDate">
						<strong class="mr-3">Data pierwszego wysłania:</strong> {{viewData.firstSendDate}}
					</div>
					<div v-if="viewData.latestSendDate">
						<strong class="mr-3">Data ostatniego wysłania:</strong> {{viewData.latestSendDate}}
					</div>
				</div>
			</div>
		</v-card-title>

		<hr class="ma-4">

		<vue-form-generator
			name="generator"
			ref="vfg"
			:model="evaluationData.model"
			:options="formGenerator.formOptions"
			:schema="formGenerator.currentSchema"
			v-if="viewData.showGenerator">
		</vue-form-generator>

		<hr class="ma-4">

		<div style="page-break-inside: avoid">
			<h3 class="mt-4 mb-4 ml-4">Rekomendacja</h3>
			<v-layout row class="mb-4 evaluation-recommendation-outer no-print">
				<v-flex xs1/>
				<v-flex xs10 :style="recommendationFrameStyle" class="evaluation-recommendation">
					<v-select
						class="ma-3"
						data-vv-name="recommending"
						data-vv-as="Rekomenduję do dofinansowania"
						:error-messages="vErrors.collect('recommending')"
						v-validate="'required'"
						:items="viewData.selectItems"
						:disabled="configuration.disabled"
						item-text="name"
						item-value="value"
						v-model="viewData.recommending"
						label="Rekomenduję do dofinansowania *"
						aria-label="recommendation"
						for="agency-project-expert-evaluation-form-recommending"
						id="agency-project-expert-evaluation-form-recommending"
					></v-select>
				</v-flex>
			</v-layout>
			<div class="d-none print-only ml-3 header-card-font-size">
				Rekomenduję do dofinansowania: <strong
					class="ml-2 header-card-font-size">{{expertRecommendation}}</strong>
			</div>
		</div>

		<v-btn @click="validateAndShowDialog" v-if="!configuration.disabled" color="success"
			class="no-print">
			<v-icon class="mr-2 white--text">send</v-icon>
			Wyślij
		</v-btn>

		<v-btn @click="saveEvaluation" v-if="!configuration.disabled" color="info" class="no-print">
			<v-icon class="mr-2 white--text">save</v-icon>
			Zapisz opinię
		</v-btn>

		<evaluation-confirmation-dialog :action="send" :dialog-model="viewData.sendEvaluationDialog"
			@close-confirmation="viewData.sendEvaluationDialog = false"
			action-label="Wyślij" content="Upewnij się, że wypełniłeś poprawnie formularz zanim zatwierdzisz opinię.
				Po zatwierdzeniu nie będzie możliwości edycji!" header="Wysyłka opinii" icon="send"/>
	</v-container>
</template>

<script>
	import {genericFormUtil} from "@/utils/genericFormUtil";
	import {Api} from "@/api";
	import {AgencyExpertEvaluation} from "@/api/generated/AgencyExpertEvaluation";
	import EvaluationConfirmationDialog from "../../EvaluationConfirmationDialog";
	import {Numbers} from "@/utils/Numbers";
	import {AgencyContest} from "@/api/generated/AgencyContest";
	import {Files} from "@/utils/Files";

	export default {
		name: "AgencyProjectExpertEvaluationForm",
		components: {EvaluationConfirmationDialog},
		watch: {
			"evaluationData.model": {
				handler () {
					this.applyGenericForm(this.configuration.form);
				},
				deep: true,
			},
		},

		data () {
			return {

				configuration: {
					form: "",
					disabled: false
				},

				evaluationData: {
					model: undefined
				},

				viewData: {
					showGenerator: false,
					agencyId: '',
					agencyProjectId: '',
					agencyProjectTitle: '',
					agencyProjectCode: '',
					controlSum: '',
					mszFundings: '',
					currencyCode: '',
					country: '',
					author: '',
          firstSendDate: '',
          latestSendDate: '',
					recommending: undefined,
					extraCountries: [],
					sendEvaluationDialog: false,
					selectItems: [
						{
							name: "TAK", value: true,
						},
						{
							name: "NIE", value: false,
						},
					]
				},

				formGenerator: {
					currentSchema: {},
					currentModel: {},
					formOptions: {
						validateAfterLoad: false,
						validateAfterChanged: true,
						validateAsync: true,
					},
					currentConditionalHashes: []
				},

				contestData: {
					id: this.$route.params.id,
					evaluationOpen: undefined,
					evaluationFinished: undefined
				}
			};
		},

		created () {
			this.loadData();
		},

		methods: {
			async loadData () {

				await Api.syncGet(this, "/agency-expert-evaluation/configuration?contestId=".concat(this.$route.params.id), successResponse => {
					this.configuration.form = genericFormUtil.decodeSchema(successResponse.schema);
					this.formGenerator.currentSchema = genericFormUtil.getSectionSchemaFromSchema(this.configuration.form, 0, this.configuration.disabled);
				});

				AgencyExpertEvaluation.get(this, this.$route.params.evaluationId, success => {
					this.viewData.agencyId = success.agencyId;
					this.viewData.agencyProjectId = success.agencyProjectId;
					this.viewData.agencyProjectTitle = success.title;
					this.viewData.agencyProjectCode = success.agencyProjectCode;
					this.viewData.controlSum = success.controlSum;
					this.viewData.mszFundings = success.mszFundings;
					this.viewData.author = success.nameSurname;
					this.viewData.country = success.country;
					this.viewData.currencyCode = success.currencyCode;
          this.viewData.firstSendDate = success.firstSendDate;
          this.viewData.latestSendDate = success.latestSendDate;
		  			this.viewData.agencyName = success.agencyName;
					this.viewData.recommending = success.evaluationSet ? success.recommending : undefined;
					this.viewData.extraCountries = [];
					success.extraCountries.forEach(country => {
						this.viewData.extraCountries.push(country);
					});
					this.evaluationData.extraCountries = success;

					if (success.evaluationStatus === 'FINISHED' || !success.authorIdentity) {
						this.configuration.disabled = true;
					}
					if (success.filledForm === null) {
						this.evaluationData.model = genericFormUtil.buildModelFromSchema(this.configuration.form).sections[0].fields;
					} else {
						this.evaluationData.model = genericFormUtil.decodeSchema(success.filledForm);
					}
					this.applyGenericForm(this.configuration.form);
					this.viewData.showGenerator = true;
				});

				AgencyContest.getState(this, this.contestData.id,
					response => {
						this.contestData.evaluationOpen = response.agencyContestStatus === 'STARTED';
						this.contestData.evaluationFinished = response.agencyContestStatus === 'FINISHED';
					});
			},

			saveEvaluation () {
				AgencyExpertEvaluation.putData(this, this.$route.params.evaluationId, this.prepareFormData(), successResponse => {
					this.$messagesBus.$emit("success", "Pomyślnie zapisano opinię", successResponse);
					this.loadData();
				}, errorResponse => {
					this.$messagesBus.$emit("error", "Nie udało się zapisać opinii", errorResponse);
				});
			},

			async send () {
				let validationPromise = await this.$refs.vfg.validate();
				if (validationPromise.length > 0) {
					return;
				}
				this.$validator.validateAll().then((result) => {
					if (result) {
						AgencyExpertEvaluation.putFinish(this, this.$route.params.evaluationId, this.prepareFormData(), successResponse => {
							this.$messagesBus.$emit("success", "Ocena została wysłana", successResponse);
							this.$router.push("/app/agency-evaluations/expert");
						}, errorResponse => {
							this.viewData.sendEvaluationDialog = false;
							this.$messagesBus.$emit("error", "Nie udało się wysłać opinii", errorResponse);
						});
					} else {
						this.viewData.sendEvaluationDialog = false;
						this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane opinii");
					}
				});
			},

			prepareFormData () {
				return {
					filledForm: genericFormUtil.encodeSchema(this.evaluationData.model),
					evaluationSet: this.viewData.recommending !== undefined,
					recommending: this.viewData.recommending === undefined ? false : this.viewData.recommending
				};
			},

			applyGenericForm (form) {
				let schema = genericFormUtil.getSectionSchemaFromSchema(form, 0, this.configuration.disabled);
				if (genericFormUtil.sectionSchemaContainsConditionalField(schema)) {
					let schemaValues = this.evaluationData.model;
					this.formGenerator.currentSchema = genericFormUtil.filterSchemaByConditionalFieldsValue(schema, schemaValues);
					let indexes = genericFormUtil.calculateIndexes(schema);
					this.formGenerator.currentConditionalHashes = genericFormUtil.getConditionalValueHashes(schema, indexes);
				} else {
					this.formGenerator.currentSchema = schema;
				}
			},

			async validateAndShowDialog () {
				let validationPromise = await this.$refs.vfg.validate();
				if (validationPromise.length > 0) {
					this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane formularza");
					return;
				}
				if (this.contestData.evaluationFinished) {
					this.$messagesBus.$emit("error", "Ocena w naborze została już zakończona");
					return;
				}
				this.$validator.validateAll().then((result) => {
					if (!result) {
						this.$messagesBus.$emit("error", "Uzupełnij poprawnie dane opinii");
					} else {
						this.viewData.sendEvaluationDialog = true;
					}
				});
			},

      openProjectView() {
        this.$router.openInNewTab('/view/agency-contest/' + this.$route.params.id + '/project/' + this.viewData.agencyId + '/' + this.viewData.agencyProjectId);
      },

			async print () {
				await Files.methods.downloadFileAsBlob(
					this,
					Files.methods.getAgencyEvaluationPrintUrl(
						this.$route.params.id,
						"expert",
						this.$route.params.evaluationId
					),
					"opinia_eksperta.pdf"
				);
			},
		},

		computed: {
			recommendationFrameStyle () {
				let color = this.viewData.recommending === true ? 'palegreen' :
					this.viewData.recommending === false ? 'palevioletred' : 'lightgray';
				return "border:2px solid " + color + "; border-radius: 25px;";
			},

			expertRecommendation () {
				let found = this.viewData.selectItems.find(item => item.value === this.viewData.recommending);
				if (!found) return "";
				return found.name;
			}
		},

		mixins: [
			Numbers
		]
	};
</script>

<style scoped>
	.header-card-font-size {
		font-size: 14px;
	}

	@media print {
		.header-card-font-size {
			font-size: 13px;
		}

		.evaluation-font-size * {
			font-size: 13px;
		}
	}
</style>